/**
 * Monthly summary defaults, the draft text built from the month's contact
 * notes, and the rules checked before signing (mirrors monthly.go).
 */
import { PRP_IRP_DOMAINS, PRP_PROGRESS_SCALE, type PrpMonthlyRollup, type PrpMonthlySummaryContent } from "@/api/prp.api";
import { parseOnto } from "./use-draft";

export function emptyMonthlySummary(month = ""): PrpMonthlySummaryContent {
  return {
    month,
    progress_services_needed: "",
    barriers: "",
    continued_justification: "",
    community_referrals: "",
    collaboration_summary: "",
    collaboration_dates: [],
  };
}

export function parseMonthlySummary(raw?: string): PrpMonthlySummaryContent {
  const c = parseOnto(raw, emptyMonthlySummary());
  return { ...c, collaboration_dates: c.collaboration_dates ?? [] };
}

/** "2026-09" -> "September 2026". */
export function monthLabel(month: string): string {
  const [y, m] = month.split("-").map(Number);
  if (!y || !m) return month;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

const progressWord = (n: number) => PRP_PROGRESS_SCALE.find((p) => p.value === Math.round(n))?.label.toLowerCase() ?? "";
const domainName = (k: string) => PRP_IRP_DOMAINS.find((d) => d.key === k)?.label ?? k;

/** Starting text for "progress & services needed" built from signed notes. */
export function progressDraftFromRollup(r: PrpMonthlyRollup): string {
  if (!r.contacts) return "";
  const hours = Math.round((r.total_minutes / 60) * 10) / 10;
  const lines = [
    `During ${monthLabel(r.month)} the consumer had ${r.contacts} PRP contact${r.contacts === 1 ? "" : "s"} (${hours} hours).`,
  ];
  for (const g of r.goals) {
    lines.push(
      `- ${g.label} (${domainName(g.domain)}): addressed in ${g.contacts} contact${g.contacts === 1 ? "" : "s"}, ` +
        `average progress ${g.avg_progress.toFixed(1)} (${progressWord(g.avg_progress)}). Latest: ${g.last_note}`,
    );
  }
  return lines.join("\n");
}

const blank = (s?: string) => !s || s.trim() === "";

export function monthlySummaryProblems(c: PrpMonthlySummaryContent): string[] {
  const p: string[] = [];
  if (!/^\d{4}-\d{2}$/.test(c.month)) p.push("Month covered");
  if (blank(c.progress_services_needed)) p.push("Consumer's progress and services needed");
  if (blank(c.barriers)) p.push("Current barriers to goal progress");
  if (blank(c.continued_justification)) p.push("Justification for continued service");
  if (blank(c.collaboration_summary)) p.push("Collaboration with service providers (or “none this month”)");
  c.collaboration_dates.forEach((d, i) => {
    if (d && !/^\d{4}-\d{2}-\d{2}$/.test(d)) p.push(`Collaboration date ${i + 1}`);
  });
  return p;
}
